import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '../../../../config/theme';
import { observer } from '@legendapp/state/react';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { globalState } from '../../../../state/globalState';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';

interface MenuItemProps {
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
}

const MenuItem = ({ icon, title, subtitle, onPress }: MenuItemProps) => (
  <TouchableOpacity style={styles.menuItem} onPress={onPress} activeOpacity={0.7}>
    <View style={styles.menuIconContainer}>
      <MaterialCommunityIcons name={icon as any} size={24} color={theme.colors.primary.default} />
    </View>
    <View style={styles.menuContent}>
      <Text style={styles.menuTitle}>{title}</Text>
      <Text style={styles.menuSubtitle}>{subtitle}</Text>
    </View>
    <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.text.tertiary} />
  </TouchableOpacity>
);

const ChecklistsSettingsHome = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const [showTemplateSheet, setShowTemplateSheet] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pendingAction, setPendingAction] = useState<'ordering' | 'fields' | null>(null);
  const empresaId = globalState.company.data.get()?.id || globalState.user.profile.get()?.empresa_id || '';
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);

  if (!premiumAccess) {
    return (
      <View style={[styles.container, styles.restrictedWrapper]}>
        <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
          <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
            <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Checklists</Text>
          <View style={{ width: 40 }} />
        </View>
        <UpgradePrompt variant="restricted" />
      </View>
    );
  }

  const goBack = () => router.back();
  const openTemplatePicker = async (action: 'ordering' | 'fields') => {
    if (!empresaId) return;
    setPendingAction(action);
    setLoadingTemplates(true);
    setShowTemplateSheet(true);
    try {
      const result = await databaseService.listItems(
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        { empresa_id: empresaId }
      );
      setTemplates(result.filter((template: any) => !template.retired_at));
    } catch (err) {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectTemplate = (template: any) => {
    setShowTemplateSheet(false);
    if (pendingAction === 'ordering') {
      router.push(`/field-ordering?templateId=${template.id}`);
    } else if (pendingAction === 'fields') {
      router.push(`/settings/checklists/fields?templateId=${template.id}`);
    }
    setPendingAction(null);
  };

  const openOrdering = () => openTemplatePicker('ordering');
  const openFields = () => openTemplatePicker('fields');
  const openTemplates = () => router.push('/settings/checklists/templates');

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={goBack}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Checklists</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <MenuItem
            icon="format-list-bulleted"
            title="Reordenar Campos"
            subtitle="Altere a ordem dos campos visíveis"
            onPress={openOrdering}
          />

          <MenuItem
            icon="pencil"
            title="Configurar Campos"
            subtitle="Criar, editar e excluir campos/grupos"
            onPress={openFields}
          />

          <MenuItem
            icon="file-document-multiple-outline"
            title="Modelos"
            subtitle="Adicionar, copiar, renomear ou excluir modelos"
            onPress={openTemplates}
          />
        </View>
      </ScrollView>

      {/* Bottom Sheet para seleção de modelo */}
      {showTemplateSheet && (
        <View style={styles.sheetOverlay}>
          <View style={styles.sheetContainer}>
            <Text style={styles.sheetTitle}>Escolha um modelo</Text>
            {loadingTemplates ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              templates.length === 0 ? (
                <Text style={styles.noDataText}>Nenhum modelo encontrado</Text>
              ) : (
                templates.map((tpl) => (
                  <TouchableOpacity
                    key={tpl.id}
                    style={styles.sheetItem}
                    onPress={() => handleSelectTemplate(tpl)}
                  >
                    <Text style={styles.sheetItemText}>{tpl.nome || 'Modelo sem nome'}</Text>
                  </TouchableOpacity>
                ))
              )
            )}
            <TouchableOpacity style={styles.sheetCancel} onPress={() => setShowTemplateSheet(false)}>
              <Text style={styles.sheetCancelText}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  restrictedWrapper: {
    justifyContent: 'flex-start',
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: theme.fontSizes.xl,
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.bold as any,
    textAlign: 'center',
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  menuIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: `${theme.colors.primary.default}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
  },
  menuContent: {
    flex: 1,
  },
  menuTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: 2,
  },
  menuSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  sheetOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#00000055',
    justifyContent: 'flex-end',
  },
  sheetContainer: {
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.lg,
    borderTopLeftRadius: theme.borderRadius.lg,
    borderTopRightRadius: theme.borderRadius.lg,
    maxHeight: '60%',
  },
  sheetTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.bold as any,
    marginBottom: theme.spacing.md,
    color: theme.colors.text.primary,
  },
  sheetItem: {
    paddingVertical: theme.spacing.sm,
  },
  sheetItemText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  sheetCancel: {
    marginTop: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    alignItems: 'center',
  },
  sheetCancelText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.primary.default,
  },
  noDataText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
});

export default ChecklistsSettingsHome; 
